import { Initiative } from '../types';

export interface InitiativeWithStats extends Initiative {
  stats: {
    goalAmount: number;
    raisedAmount: number;
    donorCount: number;
  };
}

/**
 * Hook to get initiative data with stats from JSON
 * @param initiative - The initiative object from JSON (can be undefined or null)
 * @returns Initiative data with stats from fallback values, or null if no initiative
 */
export function useInitiativeData(initiative: Initiative | undefined | null): InitiativeWithStats | null {
  if (!initiative) {
    return null;
  }

  return {
    ...initiative,
    stats: {
      goalAmount: initiative.fallbackGoal || 0,
      raisedAmount: initiative.fallbackRaised || 0,
      donorCount: 0, // Not tracked in JSON
    }
  };
}

/**
 * Hook to get initiative by slug from initiatives section
 * @param slug - The URL slug of the initiative
 * @param initiatives - The initiatives section from site content
 * @returns Initiative data with stats, or null if not found
 */
export function useInitiativeBySlug(
  slug: string | undefined,
  initiatives: { current: Initiative[]; past: Initiative[]; upcoming: Initiative[] } | undefined
): InitiativeWithStats | null {
  // Find the initiative by slug across all initiative arrays
  const initiative = slug && initiatives
    ? [
        ...initiatives.current,
        ...initiatives.past,
        ...initiatives.upcoming
      ].find((init) => init.slug === slug)
    : undefined;

  // Use the hook to get stats from JSON
  const initiativeData = useInitiativeData(initiative);

  return initiativeData;
}
